/**
 * 콘텐츠 정보 수정 — 제목·태그, 웹이면 주소까지.
 *
 * **노출 시간은 다루지 않는다.** 한 콘텐츠가 여러 플레이리스트에 들어가고 시간은
 * 편성마다 다르다. 콘텐츠 쪽에 시간을 두고 고치게 하면 "어느 편성의 시간인가" 에
 * 답할 수 없다 — 시간은 플레이리스트 편집기에서만 정한다.
 *
 * 템플릿의 **내용**은 `TemplateEditor` 가 고친다. 여기는 어떤 종류든 공통으로 가진
 * 정보만 다룬다. 이미지·영상의 파일 자체는 바꾸지 않는다 — 파일이 달라지면 새
 * 콘텐츠이고(체크섬이 곧 정체다), 새로 올려 편성에서 갈아 끼우는 편이 무엇이
 * 언제 바뀌었는지 남는다.
 */
import { useRef, useState, type ReactNode } from 'react';
import type { Asset, SignageOneClient } from '../api/client.js';
import { ErrorNote, Modal } from '../components/ui.js';
import { HelpTip } from '../components/HelpTip.js';
import { DateTimeField } from '../components/DateField.js';
import { useAction } from '../hooks.js';
import { readValidity, toLocalInput } from '../validity.js';
import { useDirty, useGuardedClose } from '../components/closeGuard.js';
import { Disclosure } from '../components/Disclosure.js';
import { focusFirstInvalid, numberProblem, parseNumberText, type NumberRule } from '../components/form/NumberInput.js';

export interface AssetForm {
  title: string;
  /** 쉼표로 구분한 태그. */
  tags: string;
  source_url: string;
  /** 게시 시작·종료 — `datetime-local` 값(이 컴퓨터의 시각). 비우면 제한 없음. */
  valid_from: string;
  valid_until: string;
  /** 소리 크기(0~100) — 영상만. */
  volume: string;
}

export type AssetPatch = Partial<Pick<Asset, 'title' | 'tags' | 'source_url' | 'valid_from' | 'valid_until' | 'volume'>>;

/** 태그 한도 — 서버 `content.ts` 의 `TAG_MAX_LENGTH`·`TAG_MAX_COUNT` 와 같은 값. */
export const ASSET_TAG_MAX_LENGTH = 40;
export const ASSET_TAG_MAX_COUNT = 30;

/**
 * 쉼표로 적은 태그 한 줄을 목록으로.
 *
 * **등록과 수정이 같은 규칙을 쓴다.** 올릴 때와 고칠 때 자르는 자리가 다르면, 올릴 때는
 * 받아 놓고 고치려 할 때 거절하는 태그가 생긴다. 같은 태그를 두 번 쓰면 하나로 치고
 * 순서는 적은 대로 둔다 — 사람이 적은 순서에 뜻이 있다.
 */
export function parseTagList(input: string): { tags: string[] } | { error: string } {
  const tags = [...new Set(input.split(',').map((tag) => tag.trim()).filter(Boolean))];
  if (tags.some((tag) => tag.length > ASSET_TAG_MAX_LENGTH)) {
    return { error: `태그는 하나에 ${ASSET_TAG_MAX_LENGTH}자까지입니다` };
  }
  if (tags.length > ASSET_TAG_MAX_COUNT) return { error: `태그는 ${ASSET_TAG_MAX_COUNT}개까지입니다` };
  return { tags };
}

export function formOf(asset: Asset): AssetForm {
  return {
    title: asset.title,
    tags: asset.tags.join(', '),
    source_url: asset.source_url ?? '',
    valid_from: toLocalInput(asset.valid_from),
    valid_until: toLocalInput(asset.valid_until),
    volume: String(asset.volume ?? 100),
  };
}

/**
 * 폼에서 **바뀐 것만** 골라 패치를 만든다.
 *
 * 전부 보내면 감사로그의 before/after 가 늘 같은 값으로 채워져, 무엇을 고쳤는지
 * 기록에서 읽을 수 없다. 잘못된 입력이면 사람이 읽을 문장을 돌려준다.
 */
/** 소리 크기 칸 — 서버(`assetPatchSchema.volume`)와 같은 범위. */
const VOLUME_RULE: NumberRule = { label: '소리 크기', min: 0, max: 100 };

/**
 * 저장할 것, 또는 막는 까닭. `field` 는 짚을 칸이다 — 칸을 빨갛게 하고 그리로 데려간다(숫자 칸만, 2026-10-20).
 */
export function buildAssetPatch(
  asset: Asset,
  form: AssetForm,
): { patch: AssetPatch } | { error: string; field?: 'volume' } {
  const title = form.title.trim();
  if (!title) return { error: '제목을 입력하세요' };
  if (title.length > 200) return { error: '제목은 200자까지입니다' };

  const parsed = parseTagList(form.tags);
  if ('error' in parsed) return { error: parsed.error };
  const { tags } = parsed;

  const patch: AssetPatch = {};
  if (title !== asset.title) patch.title = title;
  if (tags.join('\n') !== asset.tags.join('\n')) patch.tags = tags;

  if (asset.type === 'video') {
    const volume = parseNumberText(form.volume);
    const problem = numberProblem(volume, VOLUME_RULE);
    if (problem || volume === null) return { error: problem ?? '소리 크기를 입력하세요', field: 'volume' };
    if (volume !== (asset.volume ?? 100)) patch.volume = volume;
  }

  if (asset.type === 'web') {
    const url = form.source_url.trim();
    if (!/^https?:\/\/\S+$/i.test(url)) return { error: '주소는 http:// 또는 https:// 로 시작해야 합니다' };
    if (url !== asset.source_url) patch.source_url = url;
  }

  const validity = readValidity(form.valid_from, form.valid_until);
  if ('error' in validity) return { error: validity.error };
  // 분 단위로 견준다 — 입력 칸은 초를 모른다. 손대지 않은 칸을 초 차이로 "바뀌었다" 고 보내지 않게.
  if (form.valid_from !== toLocalInput(asset.valid_from)) patch.valid_from = validity.valid_from;
  if (form.valid_until !== toLocalInput(asset.valid_until)) patch.valid_until = validity.valid_until;

  return { patch };
}

export function AssetEditModal({
  client,
  asset,
  onClose,
  onSaved,
}: {
  client: SignageOneClient;
  asset: Asset;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const [form, setForm] = useState<AssetForm>(() => formOf(asset));
  /* 고친 것이 있으면 닫기 전에 묻는다. */
  const close = useGuardedClose(useDirty(form), onClose);
  const [invalid, setInvalid] = useState<string | null>(null);
  /** 저장을 막은 칸 — 빨갛게 짚는다(고치면 풀린다). */
  const [invalidField, setInvalidField] = useState<'volume' | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const action = useAction();

  const set = (patch: Partial<AssetForm>): void => {
    setForm((current) => ({ ...current, ...patch }));
    setInvalid(null);
    setInvalidField(null);
  };

  // 플레이리스트와, 콘텐츠를 직접 트는 스케줄 규칙을 함께 센다(`used_in`).
  const inPlaylists = asset.used_in?.length ?? 0;

  return (
    <Modal title="콘텐츠 정보 수정" onClose={close}>
      <form
        ref={formRef}
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          const result = buildAssetPatch(asset, form);
          if ('error' in result) {
            setInvalid(result.error);
            setInvalidField(result.field ?? null);
            if (result.field) focusFirstInvalid(formRef.current);
            return;
          }
          // 바뀐 것이 없으면 왕복하지 않는다. 감사로그에 빈 수정이 쌓이지 않게.
          if (Object.keys(result.patch).length === 0) {
            onClose();
            return;
          }
          void action.run(() => client.updateAsset(asset.id, result.patch), onSaved, '콘텐츠 정보를 저장했습니다');
        }}
      >
        {invalid ? <ErrorNote message={invalid} onDismiss={() => setInvalid(null)} /> : null}
        {action.error ? <ErrorNote message={action.error} onDismiss={action.clearError} /> : null}

        <label className="field">
          <span>
            제목
            <HelpTip text="콘솔에서 콘텐츠를 구분하는 이름이며, 화면에는 표시되지 않습니다." label="제목 도움말" />
          </span>
          <input
            className="input"
            value={form.title}
            maxLength={200}
            onChange={(event) => set({ title: event.target.value })}
            autoFocus
            required
          />
        </label>

        {asset.type === 'web' ? (
          <label className="field">
            <span>
              URL
              <HelpTip text="내부망 주소는 등록할 수 없습니다." label="URL 도움말" />
            </span>
            <input
              className="input"
              type="url"
              value={form.source_url}
              onChange={(event) => set({ source_url: event.target.value })}
              required
            />
            {inPlaylists > 0 ? (
              <small className="hint">편성 {inPlaylists}곳에서 사용 중 · 저장하면 화면에 바로 반영됩니다.</small>
            ) : null}

          </label>
        ) : null}

        {asset.type === 'video' ? (
          <label className="field">
            <span>
              소리 크기
              <HelpTip
                text="이 영상의 소리를 얼마나 크게 낼지 정합니다(0~100). 0 이면 소리 없이 나갑니다. 화면 설정의 음량과 곱해지고, 기기·TV 의 음량보다 크게는 나오지 않습니다."
                label="소리 크기 도움말"
              />
            </span>
            <span className="flex items-center gap-3">
              <input
                className="min-w-0 flex-1"
                type="range"
                min={0}
                max={100}
                step={5}
                value={Number(form.volume) || 0}
                aria-label="소리 크기"
                onChange={(event) => set({ volume: event.target.value })}
              />
              <input
                className="input w-20"
                inputMode="numeric"
                value={form.volume}
                aria-label="소리 크기 숫자"
                aria-invalid={invalidField === 'volume' || undefined}
                onChange={(event) => set({ volume: event.target.value })}
              />
            </span>
            <small className="hint">{form.volume.trim() === '0' ? '소리 없이 나갑니다.' : '100 이 원래 크기입니다.'}</small>
          </label>
        ) : null}

        <label className="field">
          <span>태그</span>
          <input
            className="input"
            value={form.tags}
            placeholder="로비, 가을 프로모션"
            onChange={(event) => set({ tags: event.target.value })}
          />
          <small className="hint">쉼표로 구분합니다.</small>
        </label>

        {/*
          게시 기간은 끝이 정해진 소재에만 적는다 — [게시 기간] 뒤로 접고(점진적 공개), 이미 적힌 값이 있으면 펼쳐서 연다.
        */}
        <Disclosure
          title="게시 기간"
          summary={validitySummary(form.valid_from, form.valid_until)}
        >
          <ValidityFields
            from={form.valid_from}
            until={form.valid_until}
            onChange={(change) => set(change)}
          />
        </Disclosure>

        <div className="form-actions">
          <button type="button" className="btn" onClick={close}>
            취소
          </button>
          <button type="submit" className="btn btn-primary" disabled={action.pending}>
            {action.pending ? '저장 중…' : '저장'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * 게시 기간 두 칸 — 콘텐츠 정보 수정·올리기·여러 개 한꺼번에가 같은 칸을 쓴다.
 *
 * 비우면 그쪽 끝에 제한이 없다. 광고 모델 계약·행사처럼 **끝이 정해진 소재**에만 적는다.
 */
export function ValidityFields({
  from,
  until,
  onChange,
}: {
  from: string;
  until: string;
  onChange: (change: { valid_from?: string; valid_until?: string }) => void;
}): ReactNode {
  return (
    <div className="field">
      <span>
        게시 기간
        <HelpTip
          text="시작 전과 종료 뒤에는 어느 플레이리스트·스케줄·캠페인에 들어 있어도 화면에 나가지 않습니다. 광고 모델 계약·행사처럼 끝이 정해진 콘텐츠에 적으세요. 화면이 오프라인이어도 종료 시각이 지나면 스스로 뺍니다."
          label="게시 기간 도움말"
        />
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <DateTimeField label="게시 시작" value={from} onChange={(next) => onChange({ valid_from: next })} />
        <span className="text-slate-400">~</span>
        <DateTimeField
          label="게시 종료"
          defaultTime="23:59"
          value={until}
          onChange={(next) => onChange({ valid_until: next })}
        />
      </div>
      <small className="hint">비우면 그쪽 끝에 제한이 없습니다. 종료 시각부터 빠집니다.</small>
    </div>
  );
}

/** [게시 기간] 이 접혀 있을 때의 한 줄 — 입력칸 값(`YYYY-MM-DDTHH:mm`)을 그대로 읽기 좋게. */
export function validitySummary(from: string, until: string): string {
  const show = (value: string): string => value.replace('T', ' ');
  if (!from && !until) return '제한 없음';
  if (from && until) return `${show(from)} ~ ${show(until)}`;
  return from ? `${show(from)} 부터` : `${show(until)} 까지`;
}
